import { config } from "@/shared/lib/config";
import {
  addMinutesToTimeHhMm,
  formatDate,
  getTodayYYYYMMDD,
  parseDateISO,
} from "@/shared/lib/date-time";
import {
  PRIVATE_LESSON_DURATION_SELECT_OPTIONS,
  PRIVATE_LESSON_SLOT_DURATION_SET,
  formatSlotDurationLaoLabel,
  type PrivateLessonSlotDurationMinutes,
} from "@/shared/lib/private-lesson-slot-duration";
import {
  formatQuarterHhMm,
  parseQuarterClock,
} from "@/shared/lib/quarter-time";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { QuarterHourMinuteSelect } from "@/shared/ui/QuarterHourMinuteSelect";
import {
  RoomBookingChargeTypeRadio,
  type RoomBookingChargeType,
} from "@/shared/ui/RoomBookingChargeTypeRadio";
import {
  SellPackageSettlementModeRadio,
  type SellPackageSettlementMode,
} from "@/shared/ui/SellPackageSettlementModeRadio";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  FormDateField,
  FormInput,
  FormPrice,
  FormRoot,
  FormTextarea,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { Calendar, ClockIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import type { BulkCreateRoomBookingsDTO } from "../../domain/contracts/bulk-room-bookings";
import {
  AssistInstructorMultiSelect,
  type AssistInstructorSelection,
} from "./AssistInstructorMultiSelect";

type RoomItem = { id: string; name: string };

const DAYS_OF_WEEK: { value: number; label: string }[] = [
  { value: 1, label: "ຈັນ" },
  { value: 2, label: "ອັງຄານ" },
  { value: 3, label: "ພຸດ" },
  { value: 4, label: "ພະທັດ" },
  { value: 5, label: "ສຸກ" },
  { value: 6, label: "ເສົາ" },
  { value: 7, label: "ອາທິດ" },
];

const slotDurZ = z.union([
  z.literal(15),
  z.literal(30),
  z.literal(45),
  z.literal(60),
  z.literal(75),
  z.literal(90),
  z.literal(105),
  z.literal(120),
  z.literal(135),
  z.literal(150),
  z.literal(165),
  z.literal(180),
]);

const DayRowSchema = z.object({
  dayOfWeek: z.number(),
  label: z.string(),
  enabled: z.boolean(),
  startTime: z.string(),
  slotDurationMinutes: slotDurZ,
  roomId: z.string(),
});

const BulkRoomBookingFormSchema = z
  .object({
    defaultRoomId: z.string().optional(),
    dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "ວັນທີບໍ່ຖືກຕ້ອງ"),
    dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "ວັນທີບໍ່ຖືກຕ້ອງ"),
    contactName: z.string().optional(),
    contactPhone: z.string().optional(),
    title: z.string().optional(),
    chargeType: z.enum(["free", "paid"]),
    amount: z.number().int().min(0).optional().nullable(),
    settlementMode: z.enum(["receipt", "credit"]).default("receipt"),
    amountPaidNow: z.coerce.number().int().min(0).default(0),
    notes: z.string().optional(),
    dayRows: z.array(DayRowSchema),
  })
  .refine((d) => d.dateFrom <= d.dateTo, {
    message: "ວັນທີສິ້ນສຸດຕ້ອງບໍ່ໃຫ້ເກີນວັນທີເລີ່ມຕົ້ນ",
    path: ["dateTo"],
  })
  .refine((d) => d.dayRows.some((r) => r.enabled), {
    message: "ຕ້ອງເລືອກວັນຢ່າງໜ້ອຍ 1 ວັນ",
    path: ["dayRows"],
  })
  .refine(
    (d) => {
      for (const r of d.dayRows) {
        if (!r.enabled) continue;
        if (!r.startTime?.trim() || r.slotDurationMinutes == null) return false;
        if (!r.roomId?.trim()) return false;
      }
      return true;
    },
    {
      message: "ວັນທີເປີດຕ້ອງມີເວລາ ແລະ ຫ້ອງ",
      path: ["dayRows"],
    },
  )
  .refine(
    (data) => {
      if (data.chargeType !== "paid") return true;
      return data.amount != null && data.amount >= 0;
    },
    {
      message: "ກະລຸນາກຳນົດຈຳນວນເງິນເມື່ອເລືອກຄິດເງິນ",
      path: ["amount"],
    },
  );

export type BulkRoomBookingFormValues = z.infer<
  typeof BulkRoomBookingFormSchema
>;

type BulkRoomBookingFormProps = {
  initialDate?: string;
  initialStartTime?: string;
  initialEndTime?: string;
  onSubmit: (values: BulkCreateRoomBookingsDTO) => void;
  submitting?: boolean;
  conflicts?: Array<{
    sessionIndex: number;
    date: string;
    startTime: string;
    endTime: string;
    message: string;
  }>;
};

const toHHmmss = (s: string) => (s.length === 5 ? `${s}:00` : s);

function toQuarterHhMmInitial(s: string | undefined): string {
  if (!s?.trim()) return "09:00";
  const raw = s.length >= 8 ? s.slice(0, 5) : s;
  const { h, m } = parseQuarterClock(raw);
  return formatQuarterHhMm(h, m);
}

function inferSlotDurationMinutes(
  initialStart?: string,
  initialEnd?: string,
): PrivateLessonSlotDurationMinutes {
  if (!initialStart?.trim() || !initialEnd?.trim()) return 60;
  const s = toQuarterHhMmInitial(initialStart);
  const e = toQuarterHhMmInitial(initialEnd);
  try {
    const start = new Date(`2000-01-01T${s}`);
    const end = new Date(`2000-01-01T${e}`);
    const mins = Math.round((end.getTime() - start.getTime()) / 60000);
    if (mins > 0 && PRIVATE_LESSON_SLOT_DURATION_SET.has(mins)) {
      return mins as PrivateLessonSlotDurationMinutes;
    }
  } catch {
    /* ignore */
  }
  return 60;
}

function getSpecDayOfWeekFromDateStr(dateStr: string): number {
  const d = parseDateISO(dateStr);
  if (!d) return 1;
  const jsDay = d.getDay();
  return jsDay === 0 ? 7 : jsDay;
}

function buildDefaultDayRows(
  initialDate?: string,
  initialStartTime?: string,
  initialEndTime?: string,
): BulkRoomBookingFormValues["dayRows"] {
  const initialDay = initialDate
    ? getSpecDayOfWeekFromDateStr(initialDate)
    : null;
  const start = toQuarterHhMmInitial(initialStartTime);
  const duration = inferSlotDurationMinutes(initialStartTime, initialEndTime);

  return DAYS_OF_WEEK.map((d) => ({
    dayOfWeek: d.value,
    label: d.label,
    enabled: initialDay === d.value,
    startTime: start,
    slotDurationMinutes: duration,
    roomId: "",
  }));
}

function countSessionsInRange(
  dateFrom: string,
  dateTo: string,
  enabledDayOfWeeks: number[],
): number {
  const from = parseDateISO(dateFrom);
  const to = parseDateISO(dateTo);
  if (!from || !to || from > to) return 0;

  const daySet = new Set(enabledDayOfWeeks);
  let count = 0;
  const current = new Date(from);
  while (current.getTime() <= to.getTime()) {
    const jsDay = current.getDay();
    const specDay = jsDay === 0 ? 7 : jsDay;
    if (daySet.has(specDay)) count += 1;
    current.setDate(current.getDate() + 1);
  }
  return count;
}

export function RoomBookingBulkCreateForm({
  initialDate,
  initialStartTime,
  initialEndTime,
  onSubmit,
  submitting,
  conflicts,
}: BulkRoomBookingFormProps) {
  const anchorDate = initialDate || getTodayYYYYMMDD();

  const defaultDayRows = useMemo(
    () => buildDefaultDayRows(initialDate, initialStartTime, initialEndTime),
    [initialDate, initialStartTime, initialEndTime],
  );

  const methods = RHF.useForm<BulkRoomBookingFormValues>({
    resolver: zodResolver(BulkRoomBookingFormSchema),
    defaultValues: {
      defaultRoomId: "",
      dateFrom: anchorDate,
      dateTo: anchorDate,
      contactName: "",
      contactPhone: "",
      title: "",
      chargeType: "free",
      amount: null,
      settlementMode: "receipt" as const,
      amountPaidNow: 0,
      notes: "",
      dayRows: defaultDayRows,
    },
  });

  const [assistInstructors, setAssistInstructors] = useState<
    AssistInstructorSelection[]
  >([]);

  const dayRows = methods.watch("dayRows") ?? defaultDayRows;
  const dateFrom = methods.watch("dateFrom");
  const dateTo = methods.watch("dateTo");
  const chargeType = methods.watch("chargeType");
  const amount = methods.watch("amount");
  const settlementMode = methods.watch("settlementMode");
  const amountPaidNow = methods.watch("amountPaidNow");
  const title = methods.watch("title");
  const contactName = methods.watch("contactName");
  const defaultRoomId = methods.watch("defaultRoomId");

  useEffect(() => {
    if (!defaultRoomId?.trim()) return;
    const rows = methods.getValues("dayRows");
    if (!rows?.some((r) => r.enabled && !r.roomId?.trim())) return;
    methods.setValue(
      "dayRows",
      rows.map((r) =>
        r.enabled && !r.roomId?.trim()
          ? { ...r, roomId: defaultRoomId.trim() }
          : r,
      ),
    );
  }, [defaultRoomId, methods]);

  const rowEndDisplay = (row: (typeof defaultDayRows)[number]): string => {
    if (!row.startTime?.trim()) return "—";
    try {
      return addMinutesToTimeHhMm(
        row.startTime.trim(),
        row.slotDurationMinutes,
      );
    } catch {
      return "—";
    }
  };

  const enabledRows = dayRows.filter((r) => r.enabled);
  const estimatedCount = useMemo(
    () =>
      countSessionsInRange(
        dateFrom || anchorDate,
        dateTo || anchorDate,
        enabledRows.map((r) => r.dayOfWeek),
      ),
    [dateFrom, dateTo, anchorDate, enabledRows],
  );

  const toggleDay = (index: number) => {
    const rows = [...(methods.getValues("dayRows") ?? defaultDayRows)];
    const row = rows[index];
    if (!row) return;
    const defaultRoom = methods.getValues("defaultRoomId")?.trim() ?? "";
    rows[index] = {
      ...row,
      enabled: !row.enabled,
      startTime: row.startTime || "09:00",
      slotDurationMinutes: PRIVATE_LESSON_SLOT_DURATION_SET.has(
        row.slotDurationMinutes,
      )
        ? row.slotDurationMinutes
        : 60,
      roomId: row.roomId || defaultRoom,
    };
    methods.setValue("dayRows", rows);
  };

  const updateDayRow = (
    index: number,
    field: keyof BulkRoomBookingFormValues["dayRows"][0],
    value: unknown,
  ) => {
    const rows = [...(methods.getValues("dayRows") ?? defaultDayRows)];
    const row = rows[index];
    if (!row) return;
    rows[index] = { ...row, [field]: value };
    methods.setValue("dayRows", rows);
  };

  const assistTotalPerSession = useMemo(
    () =>
      assistInstructors.reduce(
        (sum, a) => sum + Math.max(0, a.pricePerSession),
        0,
      ),
    [assistInstructors],
  );
  const roomUnitAmount =
    chargeType === "paid" && amount != null ? Math.max(0, amount) : 0;
  const estimatedRoomTotal = roomUnitAmount * estimatedCount;
  const estimatedAssistTotal = assistTotalPerSession * estimatedCount;
  const estimatedBillingTotal = estimatedRoomTotal + estimatedAssistTotal;

  const handleSubmit = (vals: BulkRoomBookingFormValues) => {
    const totalAmount = estimatedBillingTotal;

    if (
      totalAmount > 0 &&
      vals.settlementMode === "credit"
    ) {
      const apn = vals.amountPaidNow ?? 0;
      if (apn > totalAmount) {
        methods.setError("amountPaidNow", {
          type: "manual",
          message: "ບໍ່ເກີນຍອດລວມ",
        });
        return;
      }
    }

    const daySchedules = vals.dayRows
      .filter((r) => r.enabled)
      .map((r) => {
        const start = (r.startTime || "09:00").trim();
        const dur = r.slotDurationMinutes;
        return {
          dayOfWeek: r.dayOfWeek,
          startTime: toHHmmss(start),
          endTime: toHHmmss(addMinutesToTimeHhMm(start, dur)),
          roomId: r.roomId.trim(),
        };
      });

    onSubmit({
      dateFrom: vals.dateFrom,
      dateTo: vals.dateTo,
      contactName: vals.contactName || undefined,
      contactPhone: vals.contactPhone || undefined,
      title: vals.title || undefined,
      chargeType: vals.chargeType,
      amount:
        vals.chargeType === "paid" && vals.amount != null
          ? vals.amount
          : undefined,
      settlementMode: vals.chargeType === "paid" ? vals.settlementMode : "receipt",
      amountPaidNow:
        totalAmount > 0 && vals.settlementMode === "credit"
          ? Math.min(Math.max(0, vals.amountPaidNow ?? 0), totalAmount)
          : undefined,
      notes: vals.notes || undefined,
      assistInstructors:
        assistInstructors.length > 0
          ? assistInstructors.map((a) => ({
              instructorId: a.id,
              pricePerSession: a.pricePerSession,
            }))
          : undefined,
      daySchedules,
    });
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="lg:col-span-2">
        <FormRoot
          methods={methods}
          onSubmit={handleSubmit}
          className="flex flex-col gap-6"
        >
          <div className="rounded-xl border bg-card p-6">
            <h3 className="mb-4 flex items-center gap-2 font-semibold text-lg">
              <span className="size-2 rounded-full bg-red-600" />
              ຂໍ້ມູນການຈອງ ແລະ ຊ່ວງວັນທີ
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FormDateField name="dateFrom" label="ຈາກວັນທີ" requiredMark />
              <FormDateField name="dateTo" label="ເຖິງວັນທີ" requiredMark />

              <FormInfiniteCombobox<RoomItem>
                name="defaultRoomId"
                label="ຫ້ອງຄ່າເລີ່ມຕົ້ນ (ໃຊ້ເຕີມວັນທີເປີດ)"
                queryKey={["master", "rooms", "lookup", "room-booking-bulk"]}
                queryFn={({ search, pageParam }) =>
                  fetchLookupForInfinite<RoomItem>(
                    `${config.apiUrl}/master/rooms/lookup`,
                    { search, pageParam },
                  )
                }
                preloadQueryFn={(id) =>
                  hydrateLookupItem<RoomItem>(
                    `${config.apiUrl}/master/rooms/lookup`,
                    id,
                  ).then((item) => item ?? null)
                }
                getLabel={(item) => item.name}
                getValue={(item) => item.id}
                placeholder="ເລືອກຫ້ອງ (ທາງເລືອກ)..."
                className="md:col-span-2"
              />

              <FormInput
                name="title"
                label="ຊື່ / ວັດຖຸປະສົງການຈອງ"
                placeholder="ຕົວຢ່າງ: ປະຊຸມ, ຝຶກຊ້ອມ"
              />
              <FormInput
                name="contactName"
                label="ຊື່ຜູ້ຈອງ"
                placeholder="ຊື່ຜູ້ຕິດຕໍ່"
              />
              <FormInput
                name="contactPhone"
                label="ເບີໂທ"
                placeholder="ເບີໂທຜູ້ຈອງ"
              />

              <div className="md:col-span-2">
                <RHF.Controller
                  name="chargeType"
                  control={methods.control}
                  render={({ field }) => (
                    <RoomBookingChargeTypeRadio
                      value={field.value as RoomBookingChargeType}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </div>

              {chargeType === "paid" && (
                <div className="space-y-4 md:col-span-2">
                  <FormPrice
                    name="amount"
                    label="ຈຳນວນເງິນຕໍ່ຄັ້ງ (ກີບ)"
                    currencySymbol="₭"
                    requiredMark
                  />
                  <RHF.Controller
                    name="settlementMode"
                    control={methods.control}
                    render={({ field }) => (
                      <SellPackageSettlementModeRadio
                        value={field.value as SellPackageSettlementMode}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    )}
                  />
                  {settlementMode === "credit" &&
                    estimatedBillingTotal > 0 &&
                    estimatedCount > 0 && (
                      <>
                        <FormPrice
                          name="amountPaidNow"
                          label="ຈຳນວນທີ່ຮັບຕອນຈອງ — 0 ຖ້າຍັງບໍ່ຮັບ (ຍອດລວມ)"
                          placeholder="0"
                          currencySymbol="₭"
                        />
                        <p className="text-muted-foreground text-xs">
                          ຍອດລວມ{" "}
                          {estimatedBillingTotal.toLocaleString("lo-LA")} ₭
                          · ຮັບແລ້ວ{" "}
                          {(amountPaidNow ?? 0).toLocaleString("lo-LA")} ₭ ·
                          ຄ້າງ{" "}
                          {Math.max(
                            0,
                            estimatedBillingTotal - (amountPaidNow ?? 0),
                          ).toLocaleString("lo-LA")}{" "}
                          ₭
                        </p>
                      </>
                    )}
                  {settlementMode === "receipt" && estimatedBillingTotal > 0 && (
                    <p className="text-muted-foreground text-xs">
                      ລວມເປັນໃບເກັບເງິນເຕັມ — ຄ່າຫ້ອງ + ຄ່າຄູຊ່ວຍ × {estimatedCount} ຄັ້ງ
                    </p>
                  )}
                </div>
              )}

              <FormTextarea
                name="notes"
                label="ໝາຍເຫດ"
                placeholder="ໝາຍເຫດເພີ່ມເຕີມ"
                className="md:col-span-2"
                rows={2}
              />
            </div>
          </div>

          <div className="rounded-xl border bg-card p-6">
            <h3 className="mb-4 flex items-center gap-2 font-semibold text-lg">
              <span className="size-2 rounded-full bg-red-600" />
              ວັນໃນອາທິດ ແລະ ເວລາ (ຫ້ອງຕໍ່ວັນ)
            </h3>

            {conflicts && conflicts.length > 0 && (
              <Alert className="mb-4 border-red-200 bg-red-50 dark:bg-red-950/20">
                <AlertDescription>
                  <p className="mb-2 font-semibold text-red-900 dark:text-red-100">
                    ພົບການຂັດແຍ້ງ {conflicts.length} ລາຍການ
                  </p>
                  <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">
                    {conflicts.map((c) => (
                      <li key={`${c.sessionIndex}-${c.date}-${c.startTime}`}>
                        ລາຍການທີ {c.sessionIndex + 1}: {formatDate(c.date)}{" "}
                        {c.startTime.slice(0, 5)}–{c.endTime.slice(0, 5)} —{" "}
                        {c.message}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="pr-4 pb-2 text-left font-medium">ວັນ</th>
                    <th className="pr-4 pb-2 text-left font-medium">ເລືອກ</th>
                    <th className="pr-4 pb-2 text-left font-medium">
                      ເວລາເລີ່ມ
                    </th>
                    <th className="pr-4 pb-2 text-left font-medium">
                      ຄວາມຍາວ (ນາທີ)
                    </th>
                    <th className="pr-4 pb-2 text-left font-medium">
                      ເວລາສິ້ນສຸດ{" "}
                      <span className="font-normal text-muted-foreground">
                        (ຄຳນວນ)
                      </span>
                    </th>
                    <th className="pb-2 text-left font-medium">ຫ້ອງ</th>
                  </tr>
                </thead>
                <tbody>
                  {dayRows.map((row, index) => (
                    <tr
                      key={row.dayOfWeek}
                      className={`border-b ${row.enabled ? "bg-muted/30" : ""}`}
                    >
                      <td className="py-2 pr-4 font-medium">{row.label}</td>
                      <td className="py-2 pr-4">
                        <button
                          type="button"
                          onClick={() => toggleDay(index)}
                          className={`rounded-md border px-3 py-1.5 font-medium text-sm transition-colors ${row.enabled
                              ? "border-red-600 bg-red-600 text-white"
                              : "border-border bg-background text-muted-foreground hover:bg-muted"
                            }`}
                        >
                          {row.enabled ? "ເລືອກແລ້ວ" : "ເລືອກ"}
                        </button>
                      </td>
                      <td className="py-2 pr-4">
                        <QuarterHourMinuteSelect
                          value={row.startTime || "09:00"}
                          onChange={(next) =>
                            updateDayRow(index, "startTime", next)
                          }
                          disabled={!row.enabled}
                          className="max-w-[200px]"
                        />
                      </td>
                      <td className="py-2 pr-4 align-top">
                        <SimpleSelect
                          value={String(row.slotDurationMinutes)}
                          onValueChange={(val) => {
                            const m = Number(val);
                            if (!PRIVATE_LESSON_SLOT_DURATION_SET.has(m))
                              return;
                            updateDayRow(
                              index,
                              "slotDurationMinutes",
                              m as PrivateLessonSlotDurationMinutes,
                            );
                          }}
                          options={PRIVATE_LESSON_DURATION_SELECT_OPTIONS}
                          className="h-9 w-full min-w-[120px] max-w-[220px]"
                          disabled={!row.enabled}
                          placeholder="ເລືອກຄວາມຍາວ"
                        />
                      </td>
                      <td className="py-2 pr-4 align-top">
                        <div className="flex min-h-9 w-full min-w-[72px] max-w-[140px] items-center rounded-md border border-input border-dashed bg-muted/50 px-3 text-muted-foreground text-sm">
                          {row.enabled ? rowEndDisplay(row) : "—"}
                        </div>
                      </td>
                      <td className="py-2">
                        <DayRoomCombobox
                          value={row.roomId}
                          onChange={(v) => updateDayRow(index, "roomId", v)}
                          disabled={!row.enabled}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {methods.formState.errors.dayRows && (
              <p className="mt-2 text-destructive text-xs">
                {methods.formState.errors.dayRows.message}
              </p>
            )}
          </div>

          <div className="rounded-xl border bg-card p-6">
            <AssistInstructorMultiSelect
              value={assistInstructors}
              onChange={setAssistInstructors}
              queryKeySuffix="room-booking-bulk-assist"
              hint="ຄູທີ່ເລືອກຈະຖືກເພີ່ມທຸກສະລັອດ · ປ້ອນລາຄາຕໍ່ຄັ້ງ (ກີບ) ເພື່ອລວມໃນບິນ"
            />
          </div>

          <div className="flex justify-end">
            <Button type="submit" isLoading={submitting}>
              {submitting
                ? "ກຳລັງບັນທຶກ..."
                : `ສ້າງການຈອງ ${estimatedCount} ລາຍການ`}
            </Button>
          </div>
        </FormRoot>
      </div>

      <div className="lg:col-span-1">
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="font-semibold text-base">ສະຫຼຸບ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                <Calendar className="size-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide">
                  ຊ່ວງວັນທີ
                </p>
                <p className="mt-0.5 font-semibold text-sm">
                  {dateFrom ? formatDate(dateFrom) : "—"}
                  {dateTo && dateTo !== dateFrom
                    ? ` – ${formatDate(dateTo)}`
                    : ""}
                </p>
                <p className="mt-1 text-muted-foreground text-xs">
                  ປະມານ {estimatedCount} ລາຍການ
                </p>
              </div>
            </div>

            {enabledRows[0] && (
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-muted">
                  <ClockIcon className="size-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    ວັນທີເປີດ ({enabledRows.length})
                  </p>
                  <ul className="mt-1 space-y-1 text-sm">
                    {enabledRows.map((row) => (
                      <li key={row.dayOfWeek}>
                        <span className="font-medium">{row.label}</span>:{" "}
                        {row.startTime} – {rowEndDisplay(row)} (
                        {formatSlotDurationLaoLabel(row.slotDurationMinutes)})
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {(title || contactName) && (
              <div>
                <p className="text-muted-foreground text-xs uppercase tracking-wide">
                  ຊື່ / ວັດຖຸປະສົງ
                </p>
                <p className="mt-0.5 font-semibold text-sm">
                  {title || contactName}
                </p>
              </div>
            )}

            {estimatedBillingTotal > 0 && (
              <div className="space-y-2">
                <p className="text-muted-foreground text-xs uppercase tracking-wide">
                  {settlementMode === "credit"
                    ? "ຍອດລວມ (ໃບເກັບເງິນ)"
                    : "ຍອດລວມ (ໃບເກັບເງິນເຕັມ)"}
                </p>
                {estimatedRoomTotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>ຄ່າຈອງຫ້ອງ</span>
                    <span>
                      {estimatedCount} × {roomUnitAmount.toLocaleString("lo-LA")}{" "}
                      = {estimatedRoomTotal.toLocaleString("lo-LA")} ₭
                    </span>
                  </div>
                )}
                {assistInstructors
                  .filter((a) => a.pricePerSession > 0)
                  .map((a) => (
                    <div
                      key={a.id}
                      className="flex justify-between text-sm"
                    >
                      <span className="truncate pr-2">
                        ຄູຊ່ວຍ {a.name}
                      </span>
                      <span className="shrink-0">
                        {a.pricePerSession.toLocaleString("lo-LA")} ×{" "}
                        {estimatedCount}
                      </span>
                    </div>
                  ))}
                <p className="border-t pt-2 font-semibold text-sm">
                  ລວມ {estimatedBillingTotal.toLocaleString("lo-LA")} ₭
                </p>
                {settlementMode === "credit" && estimatedCount > 0 && (
                  <p className="text-muted-foreground text-xs">
                    ຮັບແລ້ວ {(amountPaidNow ?? 0).toLocaleString()} ₭ · ຄ້າງ{" "}
                    {Math.max(
                      0,
                      estimatedBillingTotal - (amountPaidNow ?? 0),
                    ).toLocaleString()}{" "}
                    ₭
                  </p>
                )}
              </div>
            )}

            {chargeType === "paid" && estimatedBillingTotal === 0 && (
              <div className="space-y-1">
                <p className="text-muted-foreground text-xs uppercase tracking-wide">
                  ຍອດລວມ
                </p>
                <p className="font-semibold text-sm">—</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function DayRoomCombobox({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className={disabled ? "pointer-events-none opacity-50" : ""}>
      <InfiniteCombobox<RoomItem>
        value={value}
        onValueChange={onChange}
        placeholder="ເລືອກຫ້ອງ"
        queryKey={["master", "rooms", "lookup", "room-booking-bulk-day"]}
        queryFn={({ search, pageParam }) =>
          fetchLookupForInfinite<RoomItem>(
            `${config.apiUrl}/master/rooms/lookup`,
            { search, pageParam },
          )
        }
        preloadQueryFn={(id) =>
          hydrateLookupItem<RoomItem>(
            `${config.apiUrl}/master/rooms/lookup`,
            id,
          ).then((item) => item ?? null)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        className="min-w-[140px]"
      />
    </div>
  );
}
